import { useConfirm } from "../context/ConfirmContext";
import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, QueryClient } from "@tanstack/react-query";
import { getCurrentTenant, updateCurrentTenant, createInvite, revokeInvite, getAuditLog, getIntegrations, getTenantUsers, updateUserRole, removeUser, resendInvite, updateUserProfile, assignManager, getDepartments, createDepartment, updateDepartment, mergeDepartment, deleteDepartment, getDepartmentMergeSuggestions, dismissDepartmentMergeSuggestion, DepartmentRow } from "../api";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { DateInput } from "../components/DateInput";
import DepartmentSelect from "../components/DepartmentSelect";
import { formatDate, formatDateTime } from "../utils/dateFormat";
import { useTenantTimezone } from "../hooks/useTenantTimezone";

const ROLES = [
  { value: "employee", label: "Employee" },
  { value: "hr", label: "HR" },
  { value: "recruiter", label: "Recruiter" },
  { value: "finance", label: "Finance" },
  { value: "admin_assistant", label: "Admin Assistant" },
  { value: "contractor", label: "Contractor" },
];


const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

type Tab = "overview" | "team" | "departments" | "audit";

export default function TenantPage() {
  const qc = useQueryClient();
  const tz = useTenantTimezone();
  const { user: me } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState<Tab>("overview");
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [saved, setSaved] = useState(false);
  const [inviteResult, setInviteResult] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const BLANK_INVITE = { name: "", email: "", role: "employee", designation: "", department: "", employee_id: "", joining_date: new Date().toISOString().split("T")[0], manager_id: "" };
  const [inviteForm, setInviteForm] = useState(BLANK_INVITE);
  function setIF(k: keyof typeof BLANK_INVITE) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setInviteForm(f => ({ ...f, [k]: e.target.value }));
  }
  function closeInviteModal() { setShowInviteModal(false); setInviteResult(null); setInviteForm(BLANK_INVITE); }

  const { data: tenant, isLoading } = useQuery({ queryKey: ["tenant"], queryFn: getCurrentTenant });
  const { data: integrations } = useQuery({ queryKey: ["integrations"], queryFn: getIntegrations });
  const { data: auditData } = useQuery({
    queryKey: ["audit"],
    queryFn: () => getAuditLog({ limit: 50 }),
    enabled: tab === "audit",
  });

  useEffect(() => {
    if (tenant) {
      setName((tenant as any).name);
      setContactEmail((tenant as any).contact_email || "");
    }
  }, [tenant]);

  const updateMutation = useMutation({
    mutationFn: (data: object) => updateCurrentTenant(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenant"] });
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const inviteMutation = useMutation({
    mutationFn: (data: object) => createInvite(data),
    onSuccess: (data: any) => {
      setInviteResult(data.invite_url);
      qc.invalidateQueries({ queryKey: ["tenant"] });
      if (data.email === null || data.email === undefined) return;
      if (data.email_sent === false) {
        toast(`Invite created but email delivery failed — share the link manually. Error: ${data.email_error || "unknown"}`, "error");
      } else {
        toast(`Invite email sent to ${data.email}`, "success");
      }
    },
    onError: (err: any) => toast(err?.response?.data?.detail || "Failed to create invite", "error"),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: number) => revokeInvite(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenant"] }),
  });

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast("Invite link copied to clipboard", "success");
    } catch {
      toast("Couldn't copy the link — please copy it manually", "error");
    }
  };

  if (isLoading) return <p style={{ color: "#64748b", padding: 40 }}>Loading...</p>;
  if (!tenant) return <p style={{ color: "#dc2626", padding: 40 }}>Could not load workspace info.</p>;

  const t = tenant as any;

  return (
    <div style={{ maxWidth: 860 }}>
      <LoadingOverlay active={revokeMutation.isPending || updateMutation.isPending || inviteMutation.isPending} />
      <div className="page-header">
        <div>
          <h1>Workspace</h1>
          <p>{t.name} · <span style={{ fontFamily: "monospace", fontSize: 12 }}>{t.domain}</span></p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="tab-pill-bar" style={{ marginBottom: 20 }}>
        {(["overview", "team", "departments", "audit"] as Tab[]).map(tname => (
          <button key={tname} onClick={() => setTab(tname)} className={tab === tname ? "active" : ""}>
            {tname === "audit" ? "Audit Log" : tname.charAt(0).toUpperCase() + tname.slice(1)}
          </button>
        ))}
      </div>

      {/* ── Overview tab ── */}
      {tab === "overview" && (
        <>
          {/* Stats */}
          <div className="grid-3col" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
            <StatBox label="Users" value={t.stats.users} color={NAVY} />
            <StatBox label="Jobs" value={t.stats.jobs} color="#7c3aed" />
            <StatBox label="Candidates" value={t.stats.candidates} color="#0891b2" />
          </div>
          {t.stats.inactive_users > 0 && (
            <p style={{ fontSize: 12, color: "#94a3b8", margin: "-8px 0 16px" }}>
              {t.stats.inactive_users} more {t.stats.inactive_users === 1 ? "member has" : "members have"} been removed —
              {" "}<a href="?tab=team" style={{ color: "#1e3a8a" }}>view and reactivate in Team →</a>
            </p>
          )}

          {/* Org info card */}
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={sectionHead}>Organisation</h3>
              {!editing && <button onClick={() => setEditing(true)} style={editBtn}>Edit</button>}
            </div>
            {editing ? (
              <div style={{ display: "grid", gap: 14 }}>
                <div>
                  <label style={labelStyle}>Organisation Name</label>
                  <div style={{ ...inputStyle, background: "#f8fafc", color: "#475569", display: "flex", alignItems: "center" }}>{name}</div>
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>
                    Managed in <a href="/settings?tab=branding" style={{ color: NAVY }}>Settings → Branding</a> (Company Name) — used everywhere across the app.
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Contact Email</label>
                  <input value={contactEmail} onChange={e => setContactEmail(e.target.value)} type="email" style={inputStyle} />
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => updateMutation.mutate({ contact_email: contactEmail })}
                    disabled={updateMutation.isPending}
                    style={{ background: NAVY, color: "white", border: "none", padding: "8px 18px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                    {updateMutation.isPending ? "Saving..." : "Save"}
                  </button>
                  <button onClick={() => setEditing(false)} style={{ background: "#f1f5f9", color: "#334155", border: "none", padding: "8px 18px", borderRadius: 7, cursor: "pointer", fontSize: 13 }}>Cancel</button>
                </div>
                {saved && <div style={{ fontSize: 13, color: "#16a34a" }}>Saved.</div>}
              </div>
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                <Row label="Name" value={t.name} />
                <Row label="Domain" value={t.domain} mono />
                <Row label="Contact Email" value={t.contact_email || "—"} />
                <Row label="Status" value={t.is_active ? "Active" : "Inactive"} color={t.is_active ? "#16a34a" : "#dc2626"} />
                <Row label="Created" value={formatDate(t.created_at, "short", tz)} />
              </div>
            )}
          </div>

          {/* Integrations status */}
          <IntegrationsStatus integrations={integrations} />
          {/* Onboarding checklist */}
          <OnboardingChecklist stats={t.stats} users={t.users} integrations={integrations} />
        </>
      )}

      {/* ── Team tab ── */}
      {tab === "team" && (
        <>
          {/* Current members */}
          <TeamMembersCard users={t.users} meId={me?.id} meRole={me?.role} onRefresh={() => qc.invalidateQueries({ queryKey: ["tenant"] })} />

          {/* Removed members — previously invisible with no way back in (bug-47) */}
          {t.inactive_users?.length > 0 && (
            <InactiveMembersCard users={t.inactive_users} onRefresh={() => qc.invalidateQueries({ queryKey: ["tenant"] })} />
          )}

          {/* Invite */}
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <h3 style={{ ...sectionHead, marginBottom: 0 }}>Invite Team Members</h3>
              <button onClick={() => setShowInviteModal(true)}
                style={{ background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, color: "white", border: "none", padding: "8px 16px", borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap" }}>
                + Add Employee
              </button>
            </div>

            {/* Pending invites */}
            {t.pending_invites?.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 8 }}>Pending Invites ({t.pending_invites.length})</div>
                <div style={{ display: "grid", gap: 6 }}>
                  {t.pending_invites.map((inv: any) => (
                    <div key={inv.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 7 }}>
                      <div>
                        <span style={{ fontSize: 13, color: "#1e293b" }}>{inv.email || "Generic link"}</span>
                        <span style={{ fontSize: 11, color: "#92400e", marginLeft: 10 }}>Expires {formatDate(inv.expires_at, "short", tz)}</span>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => handleCopy(`${window.location.origin}/invite/${inv.token}`)} style={{ background: "#f1f5f9", border: "none", padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11 }}>Copy</button>
                        <button onClick={() => revokeMutation.mutate(inv.id)} style={{ background: "#fee2e2", color: "#dc2626", border: "none", padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11 }}>Revoke</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Add Employee Modal ── */}
          {showInviteModal && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center" }}
              onClick={closeInviteModal}>
              <div style={{ background: "white", borderRadius: 14, padding: 28, width: "min(560px, calc(100vw - 32px))", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 24px 64px rgba(0,0,0,0.25)" }}
                onClick={e => e.stopPropagation()}>

                {!inviteResult ? (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
                      <div>
                        <h2 style={{ margin: 0, color: NAVY, fontSize: 18, fontWeight: 700 }}>Add Employee</h2>
                        <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>An invite link will be generated after saving.</p>
                      </div>
                      <button onClick={closeInviteModal} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8", lineHeight: 1 }}>✕</button>
                    </div>

                    <form onSubmit={e => {
                      e.preventDefault();
                      if (!inviteForm.email.trim()) return;
                      if (!inviteForm.name.trim()) return;
                      const payload: any = { email: inviteForm.email.trim(), name: inviteForm.name.trim(), role: inviteForm.role };
                      if (inviteForm.designation) payload.designation = inviteForm.designation;
                      if (inviteForm.department) payload.department = inviteForm.department;
                      if (inviteForm.joining_date) payload.joining_date = inviteForm.joining_date;
                      if (inviteForm.employee_id) payload.employee_id = inviteForm.employee_id;
                      if (inviteForm.manager_id) payload.manager_id = Number(inviteForm.manager_id);
                      inviteMutation.mutate(payload);
                    }} style={{ display: "grid", gap: 14 }}>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Full Name *</label>
                          <input required value={inviteForm.name} onChange={setIF("name")} placeholder="Jane Smith" style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>Work Email *</label>
                          <input required type="email" value={inviteForm.email} onChange={setIF("email")} placeholder="jane@company.com" style={inputStyle} />
                        </div>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Role *</label>
                          <select value={inviteForm.role} onChange={setIF("role")} style={inputStyle}>
                            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                            <option value="admin">Admin</option>
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>Employee ID <span style={{ fontWeight: 400, color: "#94a3b8" }}>(auto if blank)</span></label>
                          <input value={inviteForm.employee_id} onChange={setIF("employee_id")} placeholder="EMP-001" style={inputStyle} />
                        </div>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Designation / Job Title</label>
                          <input value={inviteForm.designation} onChange={setIF("designation")} placeholder="Software Engineer" style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>Department</label>
                          <DepartmentSelect
                            value={inviteForm.department}
                            onChange={name => setInviteForm(f => ({ ...f, department: name }))}
                          />
                        </div>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <div>
                          <label style={labelStyle}>Joining Date</label>
                          <DateInput value={inviteForm.joining_date} onChange={setIF("joining_date")} style={inputStyle} />
                        </div>
                        <div>
                          <label style={labelStyle}>Reports To (Manager)</label>
                          <select value={inviteForm.manager_id} onChange={setIF("manager_id")} style={inputStyle}>
                            <option value="">— No manager —</option>
                            {(t.users || []).map((u: any) => (
                              <option key={u.id} value={u.id}>{u.name}{u.designation ? ` · ${u.designation}` : ""}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 6 }}>
                        <button type="button" onClick={closeInviteModal} style={{ background: "white", border: "1px solid #e2e8f0", color: "#64748b", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>Cancel</button>
                        <button type="submit" disabled={inviteMutation.isPending}
                          style={{ background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, color: "white", border: "none", padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
                          {inviteMutation.isPending ? "Adding…" : "Add & Generate Link"}
                        </button>
                      </div>
                    </form>
                  </>
                ) : (
                  <div style={{ textAlign: "center", padding: "8px 0" }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                    <h3 style={{ margin: "0 0 6px", color: NAVY }}>Employee added!</h3>
                    <p style={{ color: "#64748b", fontSize: 13, margin: "0 0 20px" }}>
                      Share this invite link with <strong>{inviteForm.name || inviteForm.email}</strong>. It expires in 7 days.
                    </p>
                    <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "12px 14px", marginBottom: 16, textAlign: "left" }}>
                      <code style={{ fontSize: 11, color: "#1e3a8a", wordBreak: "break-all" }}>{inviteResult}</code>
                    </div>
                    <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                      <button onClick={() => { navigator.clipboard.writeText(inviteResult); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                        style={{ background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, color: "white", border: "none", padding: "10px 20px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
                        {copied ? "Copied!" : "Copy Link"}
                      </button>
                      <button onClick={() => { setInviteResult(null); setInviteForm(BLANK_INVITE); }}
                        style={{ background: "white", border: "1px solid #e2e8f0", color: "#64748b", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>Add Another</button>
                      <button onClick={closeInviteModal}
                        style={{ background: "white", border: "1px solid #e2e8f0", color: "#64748b", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>Done</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Departments tab ── */}
      {tab === "departments" && <DepartmentsPanel />}

      {/* ── Audit Log tab ── */}
      {tab === "audit" && (
        <div style={card}>
          <h3 style={sectionHead}>Audit Log</h3>
          {!auditData ? (
            <p style={{ color: "#64748b", fontSize: 14 }}>Loading...</p>
          ) : (
            <>
              <p style={{ fontSize: 12, color: "#94a3b8", marginBottom: 12 }}>Last {auditData.logs.length} events · Total: {auditData.total}</p>
              <div style={{ display: "grid", gap: 1 }}>
                {auditData.logs.map((l: any) => (
                  <div key={l.id} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "10px 12px", background: "#f8fafc", borderRadius: 6, borderLeft: `3px solid ${actionColor(l.action)}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: actionColor(l.action), background: actionBg(l.action), padding: "2px 8px", borderRadius: 20 }}>{l.action}</span>
                        <span style={{ fontSize: 11, color: "#64748b", overflowWrap: "anywhere" }}>{l.user_email}</span>
                      </div>
                      {l.details && (
                        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2, overflowWrap: "anywhere" }}>
                          {Object.entries(l.details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`).join(" · ")}
                        </div>
                      )}
                    </div>
                    <span style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>
                      {formatDateTime(l.created_at, tz)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function IntegrationsStatus({ integrations }: { integrations: any }) {
  if (!integrations) return null;
  const li = integrations.linkedin;
  const smtp = integrations.smtp;
  return (
    <div style={{ ...card, marginBottom: 16 }}>
      <h3 style={sectionHead}>Integrations</h3>
      <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <IntegRow icon="💼" label="LinkedIn" configured={li?.configured}
          detail={li?.configured ? `Posting as ${li.person_urn}` : "Not connected"}
          link="/settings" linkLabel="Configure" />
        <IntegRow icon="✉️" label="Email / SMTP" configured={smtp?.configured}
          detail={smtp?.configured ? `${smtp.sender_email}` : "Not configured"}
          link="/settings" linkLabel="Configure" />
      </div>
    </div>
  );
}

function IntegRow({ icon, label, configured, detail, link, linkLabel }: any) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 8, background: configured ? "#f0fdf4" : "#fff7ed", border: `1px solid ${configured ? "#86efac" : "#fde68a"}` }}>
      <span style={{ fontSize: 22 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>{label}</div>
        <div style={{ fontSize: 11, color: "#64748b" }}>{detail}</div>
      </div>
      {!configured && <a href={link} style={{ fontSize: 12, color: "#1d4ed8", textDecoration: "none", fontWeight: 600 }}>{linkLabel} →</a>}
      {configured && <span style={{ fontSize: 13, color: "#16a34a", fontWeight: 700 }}>✓</span>}
    </div>
  );
}

function OnboardingChecklist({ stats, users, integrations }: { stats: any; users: any[]; integrations: any }) {
  const li = integrations?.linkedin;
  const smtp = integrations?.smtp;
  const items = [
    { label: "Workspace created", done: true },
    { label: "First team member signed in", done: users.length > 0 },
    { label: "Configure email (SMTP) — Gmail requires an App Password", done: smtp?.configured, link: "/settings?tab=email", hint: "Settings → Email & SMTP → see Gmail App Password guide" },
    { label: "Connect Gmail inbox for CV intake", done: integrations?.gmail_connected, link: "/settings?tab=email", hint: "Settings → Email & SMTP → Step 3" },
    { label: "Connect LinkedIn account for job posting", done: li?.configured, link: "/settings?tab=linkedin", hint: "Settings → LinkedIn → Step 2" },
    { label: "Create first job", done: stats.jobs > 0 },
    { label: "Receive first CV", done: stats.candidates > 0 },
    { label: "Invite a team member", done: users.length > 1, link: "?tab=team" },
  ];
  const allDone = items.every(i => i.done);
  if (allDone) return null;
  return (
    <div style={{ ...card, border: `1px solid ${TEAL}`, background: "#f0fdf9" }}>
      <h3 style={{ ...sectionHead, borderBottomColor: TEAL }}>Getting Started</h3>
      <div style={{ display: "grid", gap: 8 }}>
        {items.map((item, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ fontSize: 16, color: item.done ? "#16a34a" : "#d1d5db", marginTop: 1, flexShrink: 0 }}>{item.done ? "✓" : "○"}</span>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14, color: item.done ? "#16a34a" : "#334155", textDecoration: item.done ? "line-through" : "none" }}>{item.label}</span>
                {!item.done && (item as any).link && <a href={(item as any).link} style={{ fontSize: 12, color: "#1d4ed8", textDecoration: "none", whiteSpace: "nowrap" }}>Set up →</a>}
              </div>
              {!item.done && (item as any).hint && (
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{(item as any).hint}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Row({ label, value, mono, color }: { label: string; value: string; mono?: boolean; color?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
      <span style={{ color: "#64748b", minWidth: 140 }}>{label}</span>
      <span style={{ fontWeight: 600, color: color || "#1e293b", fontFamily: mono ? "monospace" : undefined }}>{value}</span>
    </div>
  );
}

function StatBox({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ textAlign: "center", padding: "20px 16px", borderRadius: 10, background: "white", border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
      <div style={{ fontSize: 32, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>{label}</div>
    </div>
  );
}

function actionColor(action: string): string {
  if (action.startsWith("user")) return "#7c3aed";
  if (action.startsWith("job")) return "#0891b2";
  if (action.startsWith("cv") || action.startsWith("application")) return "#059669";
  if (action.startsWith("settings") || action.startsWith("tenant")) return "#d97706";
  if (action.startsWith("invite")) return "#db2777";
  return "#64748b";
}
function actionBg(action: string): string {
  if (action.startsWith("user")) return "#ede9fe";
  if (action.startsWith("job")) return "#e0f2fe";
  if (action.startsWith("cv") || action.startsWith("application")) return "#d1fae5";
  if (action.startsWith("settings") || action.startsWith("tenant")) return "#fef9c3";
  if (action.startsWith("invite")) return "#fce7f3";
  return "#f1f5f9";
}


function TeamMembersCard({ users, meId, meRole, onRefresh }: { users: any[]; meId?: number; meRole?: string; onRefresh: () => void }) {
  const tz = useTenantTimezone();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editingUser, setEditingUser] = useState<any>(null);
  const [profile, setProfile] = useState({ designation: "", department: "", employee_id: "", joining_date: "", manager_id: "" });

  const [roleErr, setRoleErr] = useState<string | null>(null);
  const roleMut = useMutation({
    mutationFn: ({ userId, role }: { userId: number; role: string }) => updateUserRole(userId, role),
    onSuccess: onRefresh,
    onError: (err: any) => {
      const msg = err?.response?.data?.detail ?? "Failed to update role";
      setRoleErr(msg);
      setTimeout(() => setRoleErr(null), 4000);
    },
  });

  const removeMut = useMutation({
    mutationFn: (userId: number) => removeUser(userId),
    onSuccess: onRefresh,
    onError: (err: any) => toast(err?.response?.data?.detail || "Failed to remove user", "error"),
  });

  const profileMut = useMutation({
    mutationFn: ({ userId, data }: { userId: number; data: object }) => updateUserProfile(userId, data),
    onSuccess: () => { onRefresh(); setEditingUser(null); },
  });

  const managerMut = useMutation({
    mutationFn: ({ userId, managerId }: { userId: number; managerId: number | null }) => assignManager(userId, managerId),
  });

  const openProfile = (u: any) => {
    setProfile({
      designation: u.designation || "",
      department: u.department || "",
      employee_id: u.employee_id || "",
      joining_date: u.joining_date ? u.joining_date.slice(0, 10) : "",
      manager_id: u.manager_id ? String(u.manager_id) : "",
    });
    setEditingUser(u);
  };

  const handleSaveProfile = (userId: number) => {
    const { manager_id, ...profileData } = profile;
    const promises: Promise<any>[] = [profileMut.mutateAsync({ userId, data: profileData })];
    const mid = manager_id ? parseInt(manager_id) : null;
    if (mid !== (editingUser?.manager_id || null)) {
      promises.push(managerMut.mutateAsync({ userId, managerId: mid }));
    }
    Promise.all(promises).then(() => { onRefresh(); setEditingUser(null); });
  };

  const roleBg = (role: string) => {
    if (role === "admin") return "#dbeafe";
    if (role === "admin_assistant") return "#ede9fe";
    if (role === "employee") return "#dcfce7";
    if (role === "contractor") return "#fff7ed";
    return "#f0fdf4";
  };
  const roleColor = (role: string) => {
    if (role === "admin") return "#1d4ed8";
    if (role === "admin_assistant") return "#7c3aed";
    if (role === "employee") return "#15803d";
    if (role === "contractor") return "#c2410c";
    return "#15803d";
  };

  return (
    <div style={card}>
      <LoadingOverlay active={removeMut.isPending || roleMut.isPending || profileMut.isPending || managerMut.isPending} />
      <h3 style={sectionHead}>Team Members ({users.length})</h3>

      {editingUser && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }}>
          <div className="modal-sheet" style={{ background: "white", borderRadius: 12, padding: 28, boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
            <h3 style={{ margin: "0 0 4px", color: NAVY }}>Edit Profile</h3>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "#64748b" }}>{editingUser.name} · {editingUser.email}</p>
            <div style={{ display: "grid", gap: 14 }}>
              <div>
                <label style={labelStyle}>Employee ID</label>
                <input value={profile.employee_id} onChange={e => setProfile(p => ({ ...p, employee_id: e.target.value }))}
                  placeholder="e.g. EMP-001" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Designation</label>
                <input value={profile.designation} onChange={e => setProfile(p => ({ ...p, designation: e.target.value }))}
                  placeholder="e.g. Software Engineer" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Department</label>
                <DepartmentSelect
                  value={profile.department}
                  onChange={name => setProfile(p => ({ ...p, department: name }))}
                />
              </div>
              <div>
                <label style={labelStyle}>Joining Date</label>
                <DateInput value={profile.joining_date} onChange={e => setProfile(p => ({ ...p, joining_date: e.target.value }))}
                  style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Manager</label>
                <select value={profile.manager_id} onChange={e => setProfile(p => ({ ...p, manager_id: e.target.value }))} style={inputStyle}>
                  <option value="">— No manager —</option>
                  {users.filter((u: any) => u.id !== editingUser?.id).map((u: any) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
              <button onClick={() => setEditingUser(null)} style={{ background: "#f1f5f9", color: "#334155", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>Cancel</button>
              <button
                onClick={() => handleSaveProfile(editingUser.id)}
                disabled={profileMut.isPending || managerMut.isPending}
                style={{ background: NAVY, color: "white", border: "none", padding: "8px 18px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                {(profileMut.isPending || managerMut.isPending) ? "Saving..." : "Save Profile"}
              </button>
            </div>
          </div>
        </div>
      )}

      {roleErr && (
        <div style={{ marginBottom: 10, padding: "8px 12px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, fontSize: 13, color: "#dc2626" }}>
          ⚠ {roleErr}
        </div>
      )}
      <div style={{ display: "grid", gap: 8 }}>
        {users.map((u: any) => (
          <div key={u.id} className="tenant-user-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#1e293b" }}>{u.name}</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{u.email}</div>
              {(u.designation || u.department || u.employee_id) && (
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                  {[u.employee_id, u.designation, u.department].filter(Boolean).join(" · ")}
                </div>
              )}
              {u.manager_name && (
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>Manager: {u.manager_name}</div>
              )}
            </div>
            <div className="tenant-user-actions" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>
                {u.last_login ? formatDate(u.last_login, "short", tz) : "Never"}
              </span>
              {u.id === meId || u.role === "admin" ? (
                <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: roleBg(u.role), color: roleColor(u.role) }}>
                  {u.role.toUpperCase()}
                </span>
              ) : (
                <select
                  value={u.role}
                  onChange={e => roleMut.mutate({ userId: u.id, role: e.target.value })}
                  style={{ border: "1px solid #d1d5db", borderRadius: 5, padding: "3px 8px", fontSize: 12, color: "#334155", cursor: "pointer", background: roleBg(u.role) }}
                >
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              )}
              <button onClick={() => openProfile(u)} style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                Profile
              </button>
              {u.id !== meId && !(meRole === "admin_assistant" && u.role === "admin") && (
                <button
                  onClick={() => {
                    confirm(`Remove ${u.name} from the workspace? They will lose access immediately.`, { confirmLabel: "Remove" }).then(ok => {
                      if (ok) removeMut.mutate(u.id);
                    });
                  }}
                  style={{ background: "#fee2e2", color: "#dc2626", border: "none", padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                >Remove</button>
              )}
            </div>
          </div>
        ))}
      </div>
      <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 12 }}>
        Contact support to change admin access.
      </p>
    </div>
  );
}

/** Removed employees (is_active=False) — used to be entirely invisible once removed,
 * with no way to bring them back (bug-47). "Resend Invite" reuses the same endpoint
 * already used for never-accepted invites: it matches on invite_pending (which
 * remove_user also sets), not is_active, so it already works for reactivating a
 * removed user without needing a separate endpoint. */
function InactiveMembersCard({ users, onRefresh }: { users: any[]; onRefresh: () => void }) {
  const { toast } = useToast();
  const [sentFor, setSentFor] = useState<number | null>(null);

  const resendMut = useMutation({
    mutationFn: (userId: number) => resendInvite(userId),
    onSuccess: (_data, userId) => {
      setSentFor(userId);
      toast("Invite sent — they'll be reactivated once they accept it.", "success");
      onRefresh();
    },
    onError: (err: any) => toast(err?.response?.data?.detail || "Failed to send invite", "error"),
  });

  return (
    <div style={{ ...card, border: "1px solid #fecaca" }}>
      <LoadingOverlay active={resendMut.isPending} />
      <h3 style={sectionHead}>Removed Members ({users.length})</h3>
      <p style={{ fontSize: 12, color: "#94a3b8", margin: "-8px 0 12px" }}>
        These accounts were removed and no longer count toward your active headcount. Resend an invite to bring one back.
      </p>
      <div style={{ display: "grid", gap: 8 }}>
        {users.map((u: any) => (
          <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{u.name || u.email}</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{u.email} · {u.role}</div>
            </div>
            <button
              disabled={resendMut.isPending}
              onClick={() => resendMut.mutate(u.id)}
              style={{ background: "white", border: "1px solid #e2e8f0", color: "#1e3a8a", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              {sentFor === u.id ? "Invite sent ✓" : "Resend Invite"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Departments management (spec: department-master-data) — list/create/rename/merge/
 * deactivate/delete, plus auto-suggested near-duplicate merges (AC4-AC7, AC13). Full
 * admin/HR parity per the PO decision of 2026-07-18 — every write here is already
 * gated require_admin_or_assistant on the backend, and this tab only appears in nav
 * for admin/admin_assistant/hr (see App.tsx). */
function DepartmentsPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const { data } = useQuery({ queryKey: ["departments"], queryFn: getDepartments });
  const { data: suggData } = useQuery({ queryKey: ["departments", "suggestions"], queryFn: getDepartmentMergeSuggestions });

  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [mergeSourceId, setMergeSourceId] = useState<number | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState<number | "">("");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["departments"] });

  const createMut = useMutation({
    mutationFn: createDepartment,
    onSuccess: () => { invalidate(); setNewName(""); toast("Department created", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Could not create department", "error"),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<{ name: string; status: string }> }) => updateDepartment(id, data),
    onSuccess: () => { invalidate(); setEditingId(null); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Update failed", "error"),
  });
  const mergeMut = useMutation({
    mutationFn: ({ id, targetId }: { id: number; targetId: number }) => mergeDepartment(id, targetId),
    onSuccess: (res) => {
      invalidate();
      setMergeSourceId(null);
      setMergeTargetId("");
      toast(`Merged — ${res.employees_moved} employee(s) moved into ${res.merged_into.name}`, "success");
    },
    onError: (e: any) => toast(e?.response?.data?.detail || "Merge failed", "error"),
  });
  const deleteMut = useMutation({
    mutationFn: deleteDepartment,
    onSuccess: () => { invalidate(); toast("Department deleted", "success"); },
    onError: (e: any) => toast(e?.response?.data?.detail || "Delete failed", "error"),
  });
  const dismissMut = useMutation({
    mutationFn: dismissDepartmentMergeSuggestion,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["departments", "suggestions"] }),
  });

  const departments = data?.departments || [];
  const suggestions = suggData?.suggestions || [];

  return (
    <div style={card}>
      <h3 style={sectionHead}>Departments</h3>

      {suggestions.length > 0 && (
        <div style={{ marginBottom: 20, padding: 14, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#92400e", marginBottom: 8 }}>Suggested merges</div>
          {suggestions.map(s => (
            <div key={`${s.keep.id}-${s.merge.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", fontSize: 13 }}>
              <span style={{ color: "#334155" }}>
                <strong>{s.merge.name}</strong> ({s.merge.employee_count}) looks like <strong>{s.keep.name}</strong> ({s.keep.employee_count})
              </span>
              <button
                onClick={() => mergeMut.mutate({ id: s.merge.id, targetId: s.keep.id })}
                disabled={mergeMut.isPending}
                style={{ background: NAVY, color: "white", border: "none", padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
              >Merge</button>
              <button
                onClick={() => dismissMut.mutate([s.keep.id, s.merge.id])}
                style={{ background: "white", color: "#64748b", border: "1px solid #e2e8f0", padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 12 }}
              >Dismiss</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="New department name"
          style={{ ...inputStyle, maxWidth: 280 }}
          onKeyDown={e => { if (e.key === "Enter" && newName.trim()) createMut.mutate(newName.trim()); }}
        />
        <button
          onClick={() => createMut.mutate(newName.trim())}
          disabled={!newName.trim() || createMut.isPending}
          style={{ background: NAVY, color: "white", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 }}
        >+ Add Department</button>
      </div>

      <div style={{ display: "grid", gap: 1 }}>
        {departments.map(d => (
          <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "#f8fafc", borderRadius: 6, opacity: d.status === "active" ? 1 : 0.6 }}>
            {editingId === d.id ? (
              <input
                autoFocus
                value={editName}
                onChange={e => setEditName(e.target.value)}
                style={{ ...inputStyle, maxWidth: 240 }}
                onKeyDown={e => {
                  if (e.key === "Enter" && editName.trim()) updateMut.mutate({ id: d.id, data: { name: editName.trim() } });
                  if (e.key === "Escape") setEditingId(null);
                }}
              />
            ) : (
              <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: "#1e293b" }}>
                {d.name}{d.status !== "active" && <span style={{ fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>(inactive)</span>}
              </div>
            )}
            <span style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>{d.employee_count} employee{d.employee_count === 1 ? "" : "s"}</span>

            {editingId === d.id ? (
              <>
                <button onClick={() => updateMut.mutate({ id: d.id, data: { name: editName.trim() } })}
                  disabled={!editName.trim() || updateMut.isPending}
                  style={{ background: NAVY, color: "white", border: "none", padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Save</button>
                <button onClick={() => setEditingId(null)}
                  style={{ background: "white", color: "#64748b", border: "1px solid #e2e8f0", padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11 }}>Cancel</button>
              </>
            ) : (
              <>
                <button onClick={() => { setEditingId(d.id); setEditName(d.name); }}
                  style={{ background: "white", color: "#334155", border: "1px solid #e2e8f0", padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11 }}>Rename</button>
                <button
                  onClick={() => updateMut.mutate({ id: d.id, data: { status: d.status === "active" ? "inactive" : "active" } })}
                  style={{ background: "white", color: "#334155", border: "1px solid #e2e8f0", padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11 }}
                >{d.status === "active" ? "Deactivate" : "Reactivate"}</button>
                <button onClick={() => setMergeSourceId(mergeSourceId === d.id ? null : d.id)}
                  style={{ background: mergeSourceId === d.id ? "#eff6ff" : "white", color: "#1e40af", border: "1px solid #bfdbfe", padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11 }}
                >Merge into…</button>
                {d.employee_count === 0 && (
                  <button
                    onClick={() => {
                      confirm(`Delete department "${d.name}"? This cannot be undone.`, { confirmLabel: "Delete" }).then(ok => {
                        if (ok) deleteMut.mutate(d.id);
                      });
                    }}
                    style={{ background: "#fee2e2", color: "#dc2626", border: "none", padding: "4px 10px", borderRadius: 5, cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                  >Delete</button>
                )}
              </>
            )}
          </div>
        ))}
        {departments.length === 0 && <p style={{ color: "#94a3b8", fontSize: 13 }}>No departments yet.</p>}
      </div>

      {mergeSourceId !== null && (
        <div style={{ marginTop: 14, padding: 14, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 13, color: "#1e3a8a" }}>
            Merge <strong>{departments.find(d => d.id === mergeSourceId)?.name}</strong> into:
          </span>
          <select
            value={mergeTargetId}
            onChange={e => setMergeTargetId(e.target.value ? Number(e.target.value) : "")}
            style={{ ...inputStyle, maxWidth: 220 }}
          >
            <option value="">— select target —</option>
            {departments.filter(d => d.id !== mergeSourceId).map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <button
            onClick={() => mergeTargetId !== "" && mergeMut.mutate({ id: mergeSourceId, targetId: mergeTargetId as number })}
            disabled={mergeTargetId === "" || mergeMut.isPending}
            style={{ background: NAVY, color: "white", border: "none", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
          >Merge</button>
          <button onClick={() => { setMergeSourceId(null); setMergeTargetId(""); }}
            style={{ background: "white", color: "#64748b", border: "1px solid #e2e8f0", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12 }}
          >Cancel</button>
        </div>
      )}
    </div>
  );
}


const card: React.CSSProperties = { background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: 24, marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" };
const sectionHead: React.CSSProperties = { color: NAVY, fontSize: 15, margin: "0 0 16px", borderBottom: `2px solid ${TEAL}`, paddingBottom: 6 };
const labelStyle: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 };
const inputStyle: React.CSSProperties = { width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, boxSizing: "border-box" };
const editBtn: React.CSSProperties = { background: "#f1f5f9", color: "#334155", border: "none", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600 };
